import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { UserPicker } from '../shared/UserPicker';
import { SupplierSelect } from '../suppliers/SupplierSelect';
import { extractDateFromISO } from '../../utils/validation';
import type { WorkOrder, WorkOrderPriority, CreateWorkOrderInput, UpdateWorkOrderInput, WmsUser } from '../../types';

interface WorkOrderFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateWorkOrderInput | UpdateWorkOrderInput) => Promise<void>;
  workOrder?: WorkOrder | null;
  isLoading?: boolean;
}

const priorityOptions: { value: WorkOrderPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

function formatUserName(user: WmsUser): string {
  if (user.firstname || user.lastname) {
    return [user.firstname, user.lastname].filter(Boolean).join(' ');
  }
  return user.username || user.email;
}

export function WorkOrderFormModal({
  open,
  onClose,
  onSubmit,
  workOrder,
  isLoading,
}: WorkOrderFormModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<WorkOrderPriority>('normal');
  const [dueDate, setDueDate] = useState('');
  const [assignedUser, setAssignedUser] = useState<WmsUser | null>(null);
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [supplierName, setSupplierName] = useState('');
  const [notes, setNotes] = useState('');

  const isEditing = !!workOrder;

  useEffect(() => {
    if (workOrder) {
      setTitle(workOrder.title);
      setDescription(workOrder.description || '');
      setPriority(workOrder.priority);
      setDueDate(extractDateFromISO(workOrder.due_date));
      // Pre-populate user if we have the info from existing work order
      if (workOrder.assigned_to_user_id && workOrder.assigned_to_user_name) {
        setAssignedUser({
          _id: workOrder.assigned_to_user_id,
          email: workOrder.assigned_to_user_name, // Fallback since we may not have full user data
          firstname: workOrder.assigned_to_user_name,
          enabled: true,
        });
      } else {
        setAssignedUser(null);
      }
      // Pre-populate supplier
      setSupplierId(workOrder.supplier_id || undefined);
      setSupplierName(workOrder.supplier_name || '');
      setNotes(workOrder.notes || '');
    } else {
      resetForm();
    }
  }, [workOrder, open]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPriority('normal');
    setDueDate('');
    setAssignedUser(null);
    setSupplierId(undefined);
    setSupplierName('');
    setNotes('');
  };

  const handleSupplierSelect = (supplier: { id: string; name: string } | null) => {
    if (supplier) {
      setSupplierId(supplier.id);
      setSupplierName(supplier.name);
    } else {
      setSupplierId(undefined);
      setSupplierName('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) return;

    const data: CreateWorkOrderInput | UpdateWorkOrderInput = {
      title,
      description: description || undefined,
      priority,
      supplier_id: supplierId || undefined,
      supplier_name: supplierName || undefined,
      due_date: dueDate || undefined,
      assigned_to_user_id: assignedUser?._id,
      assigned_to_user_name: assignedUser ? formatUserName(assignedUser) : undefined,
      notes: notes || undefined,
    };

    try {
      await onSubmit(data);
      toast.success(isEditing ? 'Work order updated successfully' : 'Work order created successfully');
      resetForm();
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} work order: ${message}`);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} work order:`, error);
      // Keep form open for retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Work Order' : 'Create Work Order'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update work order details.'
              : 'Create a new work order to track fulfillment.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Title *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter work order title"
              required
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              className="mt-1"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Priority</label>
              <Select value={priority} onValueChange={(v) => setPriority(v as WorkOrderPriority)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Due Date</label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Assigned To</label>
            <div className="mt-1">
              <UserPicker
                value={assignedUser}
                onChange={setAssignedUser}
                placeholder="Search for assignee..."
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Supplier</label>
            <div className="mt-1">
              <SupplierSelect
                value={supplierId}
                onSelect={handleSupplierSelect}
                placeholder="Select a supplier..."
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes..."
              className="mt-1"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !title}>
              {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
